export type CoolDownStretch = {
  name: string
  instruction: string
  duration: string
}

export type CoolDownProtocol = {
  duration_minutes: number
  cardio: { instruction: string }
  stretches: CoolDownStretch[]
}

export type CoolDownStep =
  | { kind: 'cardio'; title: string; instruction: string; duration: string }
  | { kind: 'stretch'; title: string; instruction: string; duration: string }

const DEFAULT_CARDIO =
  '3 minutes easy walk or stationary bike. Nasal breathing, no hard effort — let heart rate drift down.'

const STRETCH_LIBRARY: { pattern: RegExp; stretch: CoolDownStretch }[] = [
  {
    pattern: /squat|lunge|leg|deadlift|hip|glute|hamstring/i,
    stretch: {
      name: 'Hip flexor stretch',
      instruction: 'Half-kneeling, tuck pelvis, lean forward until you feel the front of the hip open.',
      duration: '30s each side',
    },
  },
  {
    pattern: /squat|lunge|leg|quad|press|step/i,
    stretch: {
      name: 'Standing quad stretch',
      instruction: 'Stand on one leg, pull heel to glute, knees together, chest tall.',
      duration: '30s each side',
    },
  },
  {
    pattern: /press|push|bench|chest|fly|dip/i,
    stretch: {
      name: 'Doorway chest stretch',
      instruction: 'Forearm on door frame, step through until chest opens — no shoulder pinch.',
      duration: '30s each side',
    },
  },
  {
    pattern: /row|pull|lat|chin|pulldown/i,
    stretch: {
      name: 'Lat side bend',
      instruction: 'Reach overhead and lean to one side, ribs down, feel the side body lengthen.',
      duration: '30s each side',
    },
  },
  {
    pattern: /shoulder|overhead|press|raise/i,
    stretch: {
      name: 'Cross-body shoulder stretch',
      instruction: 'Pull one arm across the chest, keep shoulder down and relaxed.',
      duration: '30s each side',
    },
  },
  {
    pattern: /core|plank|twist|carry/i,
    stretch: {
      name: "Child's pose",
      instruction: 'Knees wide, sit hips back, arms long — breathe into the lower back.',
      duration: '45s',
    },
  },
]

function uniqueStretches(stretches: CoolDownStretch[], limit: number): CoolDownStretch[] {
  const seen = new Set<string>()
  const out: CoolDownStretch[] = []
  for (const stretch of stretches) {
    const key = stretch.name.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(stretch)
    if (out.length >= limit) break
  }
  return out
}

export function inferStretchesForExercises(
  exercises: { exercise_name: string }[],
  limit = 3,
): CoolDownStretch[] {
  const matched: CoolDownStretch[] = []
  for (const exercise of exercises) {
    const name = exercise.exercise_name ?? ''
    for (const entry of STRETCH_LIBRARY) {
      if (entry.pattern.test(name)) matched.push(entry.stretch)
    }
  }

  const picked = uniqueStretches(matched, limit)
  if (picked.length >= 2) return picked

  return uniqueStretches(
    [
      ...picked,
      STRETCH_LIBRARY[0].stretch,
      STRETCH_LIBRARY[3].stretch,
      {
        name: 'Thoracic rotation',
        instruction: 'On all fours, one hand behind head, rotate elbow to ceiling, hips still.',
        duration: '30s each side',
      },
    ],
    limit,
  )
}

export function buildFallbackCoolDown(exercises: { exercise_name: string }[]): CoolDownProtocol {
  return {
    duration_minutes: 5,
    cardio: { instruction: DEFAULT_CARDIO },
    stretches: inferStretchesForExercises(exercises, 3),
  }
}

function parseStretch(raw: unknown): CoolDownStretch | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const name = String(row.name ?? '').trim()
  const instruction = String(row.instruction ?? '').trim()
  const duration = String(row.duration ?? '30s').trim()
  if (!name || !instruction) return null
  return { name, instruction, duration }
}

export function normalizeCoolDownProtocol(
  raw: unknown,
  exercises: { exercise_name: string }[],
): CoolDownProtocol | null {
  if (!exercises.length) return null

  if (!raw || typeof raw !== 'object') {
    return buildFallbackCoolDown(exercises)
  }

  const row = raw as Record<string, unknown>
  const cardioInstruction = String((row.cardio as Record<string, unknown> | undefined)?.instruction ?? row.cardio ?? '').trim()
  const stretches = Array.isArray(row.stretches)
    ? row.stretches.map(parseStretch).filter((s): s is CoolDownStretch => s != null)
    : []

  return {
    duration_minutes: Math.min(10, Math.max(3, Number(row.duration_minutes ?? 5) || 5)),
    cardio: { instruction: cardioInstruction || DEFAULT_CARDIO },
    stretches: stretches.length >= 2 ? stretches.slice(0, 3) : inferStretchesForExercises(exercises, 3),
  }
}

export function coolDownSteps(protocol: CoolDownProtocol): CoolDownStep[] {
  const cardioMinutes = Math.max(2, Math.min(4, protocol.duration_minutes - protocol.stretches.length))
  return [
    {
      kind: 'cardio',
      title: 'Easy cardio flush',
      instruction: protocol.cardio.instruction,
      duration: `${cardioMinutes} min`,
    },
    ...protocol.stretches.map((stretch) => ({
      kind: 'stretch' as const,
      title: stretch.name,
      instruction: stretch.instruction,
      duration: stretch.duration,
    })),
  ]
}

export function hasGuidedCoolDown(
  exercises: { exercise_name: string }[],
  protocol: CoolDownProtocol | null | undefined,
): protocol is CoolDownProtocol {
  return exercises.length > 0 && protocol != null && protocol.stretches.length >= 2
}
